import subContext from './sub-context.js'
import buildReferenceList from './referenced-list.js'
import buildLookupList from './lookup-list.js'
import ListFilter from './list-filter.js'
import {stateMerge} from './helper.js'

/**
 * List a resource.
 *
 * @param {ModuleBuilderContext} context
 * @param {boolean} recurse
 *
 * @return {object}
 */
export default function moduleList (context, recurse = true) {
  const {resource, $name, $namespace, $parentNamespace} = context;
  const uc = resource.name.toUpperCase();
  const RESET = uc + '_LIST_RESET';
  const SET_ITEMS = uc + '_LIST_SET_ITEMS';
  const SET_ERROR = uc + '_LIST_SET_ERROR';
  const SET_VIEW = uc + '_LIST_SET_VIEW';
  const TOGGLE_LOADING = uc + '_LIST_TOGGLE_LOADING';
  const UPDATE_FILTER = uc + '_UPDATE_FILTER';

  const modules = {}
  const operation = resource.operations.list
  if (!operation) {
    throw new Error('Logic exception? Resource has no list URL: ' + resource.name)
  }
  if (recurse) {
    const references = buildReferenceList(subContext(context, 'references'), operation.output.items.properties)
    if (references !== undefined) {
      modules.references = references
    }
    const lookup = buildLookupList(subContext(context, 'lookup'), operation.output.items.properties)
    if (lookup !== undefined) {
      modules.lookup = lookup
    }
  }

  const filters = new ListFilter(operation)

  return {
    namespaced: true,
    modules: {
      ...modules
    },
    state: {
      $name,
      $namespace,
      $parentNamespace,
      error: '',
      isLoading: false,
      items: [],
      view: {},
      filter: filters.defaultFilter({
        totalItems: NaN,
        itemsPerPage: 20,
        page: 1,
      }),
    },
    actions: {
      default: ({ commit, dispatch, state, rootGetters}, page = undefined) => {
        let companyFilterHack = false
        if (page === undefined) {
          // Initialize with base URL.
          page = filters.buildUrl(state.filter)
          // FIXME: company filter hack - should replace with "global filter".
          if (!state.filter.company && rootGetters['authentication/selectedCompanyId']) {
            page += '&company=' + rootGetters['authentication/selectedCompanyId']
            companyFilterHack = true
          }
        }
        commit(TOGGLE_LOADING)
        return new Promise((resolve, reject) => {
          dispatch('api/fetch', {uri: page}, { root: true })
            .then(response => response.json())
            .then((data) => {
              commit(TOGGLE_LOADING)
              commit(SET_ERROR, '')
              commit(SET_ITEMS, data['hydra:member'])
              commit(SET_VIEW, data['hydra:view'])

              const filter = filters.parseUrl(data['hydra:view'] ? data['hydra:view']['@id'] : page)
              if (companyFilterHack) {
                delete filter.company
              }
              filter.totalItems = parseInt(data['hydra:totalItems']) || NaN
              // FIXME: can we implement this somehow better?
              if (isNaN(filter.itemsPerPage)) {
                filter.itemsPerPage = 20
              }
              // This happens when there are less than one page of items.
              if (isNaN(filter.page)) {
                filter.page = 1
              }

              commit(UPDATE_FILTER, filter)
              if (recurse) {
                dispatch('references/loadFor', data['hydra:member'])
              }
              resolve(data)
            })
            .catch((e) => {
              commit(TOGGLE_LOADING)
              commit(SET_ERROR, e.message)
              reject(e)
            })
        })
      },
      reset: ({commit}) => {
        commit(RESET)
      },
      setFilter: ({ commit, dispatch }, payload) => {
        commit(UPDATE_FILTER, payload)
        dispatch('default')
      },
    },
    getters: {
      error: state => state.error,
      isLoading: state => state.isLoading,
      items: state => state.items,
      view: state => state.view,
      filter: state => state.filter,
      hasFilter: (name) => state => filters.has(name),
      filterDef: () => filters,
    },
    mutations: {
      [RESET] (state) {
        Object.assign(state, {
          error: '',
          isLoading: false,
          items: [],
          view: {},
          filter: filters.defaultFilter({
            totalItems: NaN,
            itemsPerPage: 20,
            page: 1,
          })
        })
      },
      [SET_ERROR] (state, error) {
        Object.assign(state, { error })
      },
      [SET_ITEMS] (state, items) {
        Object.assign(state, { items })
      },
      [TOGGLE_LOADING] (state) {
        Object.assign(state, { isLoading: !state.isLoading })
      },
      [SET_VIEW] (state, view) {
        Object.assign(state, { view })
      },
      [UPDATE_FILTER] (state, filter) {
        stateMerge(state, { filter })
      }
    }
  }
}
